import { useEffect, useRef, useState } from "react";
import {
  autodartsStatusLabel,
  isAutodartsTakeout,
  parseAutodartsState,
  type AutodartsBoardState,
  type DartThrow,
} from "@shared/index";

export type AutodartsUiStatus = {
  reachable: boolean;
  label: string;
  error: string | null;
};

const IDLE: AutodartsUiStatus = { reachable: false, label: "Verbinde…", error: null };

export function useAutodartsInput(opts: {
  enabled: boolean;
  url: string;
  playing: boolean;
  visitLength: number;
  onDart: (dart: DartThrow) => void;
  onTakeout: () => void;
}): AutodartsUiStatus {
  const [ui, setUi] = useState<AutodartsUiStatus>(IDLE);
  const appliedRef = useRef(0);
  const lastLenRef = useRef(0);
  const armedRef = useRef(false);
  const onDartRef = useRef(opts.onDart);
  const onTakeoutRef = useRef(opts.onTakeout);
  const visitRef = useRef(opts.visitLength);
  onDartRef.current = opts.onDart;
  onTakeoutRef.current = opts.onTakeout;
  visitRef.current = opts.visitLength;

  useEffect(() => {
    appliedRef.current = 0;
    lastLenRef.current = 0;
    armedRef.current = false;
    setUi(IDLE);
  }, [opts.enabled, opts.url]);

  useEffect(() => {
    if (!opts.enabled || !opts.playing) return;
    const desktop = window.steeldartDesktop;
    let cancelled = false;

    const ingest = (state: AutodartsBoardState) => {
      const throws = state.throws;
      const len = throws.length;
      if (!armedRef.current) {
        appliedRef.current = len;
        lastLenRef.current = len;
        armedRef.current = true;
        return;
      }
      const takeout = isAutodartsTakeout(state) || len < lastLenRef.current || (len === 0 && lastLenRef.current > 0);
      if (takeout) {
        if (visitRef.current > 0 && visitRef.current < 3) onTakeoutRef.current();
        appliedRef.current = 0;
        lastLenRef.current = len;
        if (len === 0) return;
      }
      if (len > appliedRef.current) {
        for (let i = appliedRef.current; i < len; i++) {
          const dart = throws[i]!;
          onDartRef.current({ segment: dart.segment, multiplier: dart.multiplier });
        }
        appliedRef.current = len;
      }
      lastLenRef.current = len;
    };

    const tick = async () => {
      try {
        let raw: unknown;
        if (desktop?.autodartsState) {
          const result = await desktop.autodartsState(opts.url);
          if (cancelled) return;
          if (!result.ok) {
            setUi({ reachable: false, label: "Nicht erreichbar", error: result.error });
            return;
          }
          raw = result.state;
        } else {
          const origin = opts.url.replace(/\/+$/, "");
          const res = await fetch(`${origin}/api/state`, { signal: AbortSignal.timeout(2000) });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          raw = await res.json();
        }
        if (cancelled) return;
        const parsed = parseAutodartsState(raw);
        ingest(parsed);
        setUi({
          reachable: true,
          label: autodartsStatusLabel(parsed, true),
          error: null,
        });
      } catch {
        if (cancelled) return;
        setUi({
          reachable: false,
          label: "Nicht erreichbar",
          error: "Autodarts Board Manager nicht erreichbar. Desktop-App im selben Netz wie die Kamera, URL prüfen.",
        });
      }
    };

    void tick();
    const id = window.setInterval(() => void tick(), 350);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [opts.enabled, opts.playing, opts.url]);

  return ui;
}
